import { useEffect, useRef, useState, useCallback } from 'react'
import { Html5Qrcode } from 'html5-qrcode'
import { XClose } from '@untitledui/icons'
import useTranslation from '@/hooks/useTranslation'
import ResponsiveModal from '@/components/ResponsiveModal'

export default function QrScannerModal({ isOpen, onClose, onScan }) {
    const { t } = useTranslation()
    const scannerRef = useRef(null)
    const startPromiseRef = useRef(null)
    const readerId = useRef(
        `qr-reader-${Math.random().toString(36).slice(2)}`
    ).current
    const [error, setError] = useState(null)

    const stopScanner = useCallback(async () => {
        const scanner = scannerRef.current
        const startPromise = startPromiseRef.current
        if (!scanner) return
        scannerRef.current = null
        startPromiseRef.current = null
        try {
            await startPromise
        } catch {
            /* ignore */
        }
        try {
            await scanner.stop()
        } catch {
            /* ignore */
        }
        try {
            scanner.clear()
        } catch {
            /* ignore */
        }
    }, [])

    useEffect(() => {
        if (!isOpen) return

        let cancelled = false

        const timer = setTimeout(async () => {
            if (cancelled) return

            const scanner = new Html5Qrcode(readerId, { verbose: false })
            scannerRef.current = scanner

            const startPromise = scanner.start(
                { facingMode: 'environment' },
                { fps: 10, qrbox: { width: 250, height: 250 } },
                (decodedText) => {
                    if (cancelled) return
                    const accepted = onScan(decodedText)
                    if (!accepted) return
                    cancelled = true
                    stopScanner().then(() => onClose())
                },
                () => {}
            )
            startPromiseRef.current = startPromise

            try {
                await startPromise
            } catch (err) {
                if (!cancelled) setError(err?.message || 'Camera access denied')
            }
        }, 50)

        return () => {
            cancelled = true
            clearTimeout(timer)

            // Capture refs locally — cleanup must await start() before stopping
            const scanner = scannerRef.current
            const startPromise = startPromiseRef.current
            scannerRef.current = null
            startPromiseRef.current = null

            if (scanner) {
                ;(async () => {
                    try {
                        await startPromise
                    } catch {
                        /* ignore */
                    }
                    try {
                        await scanner.stop()
                    } catch {
                        /* ignore */
                    }
                    try {
                        scanner.clear()
                    } catch {
                        /* ignore */
                    }
                })()
            }
        }
    }, [isOpen])

    const handleClose = useCallback(async () => {
        await stopScanner()
        setError(null)
        onClose()
    }, [onClose, stopScanner])

    return (
        <ResponsiveModal
            isOpen={isOpen}
            onClose={handleClose}
            desktopClassName="w-[90vw] max-w-[720px]"
        >
            <div className="flex items-center justify-between p-[16px]">
                <span className="text-[16px] font-semibold">
                    {t('warranty.scanQrCode')}
                </span>
                <button
                    onClick={handleClose}
                    className="cursor-pointer text-dark-blue/40 hover:text-dark-blue"
                >
                    <XClose size={20} />
                </button>
            </div>

            <div className="px-[16px]">
                <p className="text-[14px] text-dark-blue/60 mb-[12px]">
                    {t('warranty.scanQrCodeDescription')}
                </p>
                <div className="relative w-full aspect-square lg:aspect-video rounded-[8px] overflow-hidden bg-background-product">
                    <div className="absolute inset-0 flex items-center justify-center">
                        <svg className="animate-spin h-6 w-6 text-dark-blue/20" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                    </div>
                    <div
                        id={readerId}
                        className="relative w-full h-full [&>video]:w-full [&>video]:h-full [&>video]:object-cover"
                        style={{ transform: 'scaleX(-1)' }}
                    />
                </div>
                {error && (
                    <p className="text-red-500 text-[13px] mt-[8px] text-center">
                        {error}
                    </p>
                )}
            </div>

            <div className="h-[16px]" />
        </ResponsiveModal>
    )
}
